import { formatPercent } from '../lib/format';

/**
 * Bounce/complaint-rate bar against warn/pause thresholds. Colour is
 * never the only signal — a text status ("Healthy"/"Warning"/"Pause")
 * always accompanies it. Below `minSample` sends auto-pause ignores the
 * rate (1 bounce in the first 3 sends is 33 %), so the bar says so
 * instead of showing a pause level that nothing acts on.
 */
export function RateBar({
  label,
  rate,
  warnAt,
  pauseAt,
  attempted,
  minSample,
}: {
  label: string;
  rate: number;
  warnAt: number;
  pauseAt: number;
  attempted: number;
  minSample: number;
}) {
  const status =
    attempted < minSample ? 'early' : rate >= pauseAt ? 'pause' : rate >= warnAt ? 'warn' : 'ok';
  const statusText =
    status === 'early'
      ? `Too few sends to judge (${attempted} of ${minSample})`
      : status === 'pause'
        ? '⛔ Pause level'
        : status === 'warn'
          ? '! Warning'
          : '✓ Healthy';
  const statusColor =
    status === 'early'
      ? 'text-muted'
      : status === 'pause'
        ? 'text-danger-text'
        : status === 'warn'
          ? 'text-warning-text'
          : 'text-success-text';
  const fillPct = Math.min(100, (rate / pauseAt) * 100);
  const warnPct = Math.min(100, (warnAt / pauseAt) * 100);

  return (
    <div>
      <div className="flex text-sm">
        <span>{label}</span>
        <span className="ml-auto font-mono font-medium">{formatPercent(rate)}</span>
      </div>
      <div className="relative h-2 bg-[#F0F0F2] rounded mt-2">
        <div
          className={`h-2 rounded ${status === 'early' ? 'bg-[#C9C9CF]' : 'bg-success-bar'}`}
          style={{ width: `${fillPct}%` }}
        />
        <div
          className="absolute -top-[3px] w-[2px] h-3.5 bg-[#B45309]"
          style={{ left: `${warnPct}%` }}
        />
        <div className="absolute -top-[3px] right-0 w-[2px] h-3.5 bg-[#B91C1C]" />
      </div>
      <div className="flex text-xs text-muted mt-1.5">
        <span className={statusColor}>{statusText}</span>
        <span className="ml-auto">
          warning {formatPercent(warnAt)} · pause {formatPercent(pauseAt)}
        </span>
      </div>
    </div>
  );
}
